import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import config from "../../../config";
import { AuthStore } from "../stores/AuthStore";

const api: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

// Attach Bearer token to every request
api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const token = AuthStore.getState().accessToken;
  if (token && cfg.headers) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// On 401 → attempt silent token refresh, then retry once
let refreshing = false;
let queue: Array<{ resolve: (t: string) => void; reject: (e: unknown) => void }> = [];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // Auth endpoints returning 401 mean bad credentials — skip refresh entirely
    // and let the original server error propagate to the caller unchanged
    if (original?.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (refreshing) {
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    refreshing = true;
    try {
      const { refreshToken } = AuthStore.getState();
      if (!refreshToken) throw new Error("No refresh token");

      const { data } = await axios.post(`${config.apiBaseUrl}/auth/refresh`, { refreshToken });
      const newAccess: string = data.data.accessToken;
      const newRefresh: string = data.data.refreshToken;

      AuthStore.getState().setTokens(newAccess, newRefresh);

      queue.forEach((p) => p.resolve(newAccess));
      queue = [];

      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (err) {
      queue.forEach((p) => p.reject(err));
      queue = [];
      AuthStore.getState().logout();
      return Promise.reject(err);
    } finally {
      refreshing = false;
    }
  },
);

export default api;
